import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type PolicyType = 'privacy' | 'terms' | 'refund' | null;
const policyContent: Record<Exclude<PolicyType, null>, {
  title: string;
  body: string;
}> = {
  privacy: {
    title: 'Privacy Policy',
    body: `Última actualización: Abril 2026

Recopilamos información personal mínima necesaria para procesar tu compra y entregarte el Business-Rescue Kit. Esto incluye tu nombre, correo electrónico y datos de pago.

No compartimos, vendemos ni alquilamos tu información personal a terceros. Tus datos de pago son procesados de forma segura a través de nuestro proveedor de pagos certificado.

Utilizamos cookies esenciales para el funcionamiento del sitio. No utilizamos cookies de rastreo publicitario.

Puedes solicitar la eliminación de tus datos en cualquier momento contactándonos por correo electrónico.

Nos reservamos el derecho de actualizar esta política. Te notificaremos de cualquier cambio significativo.`
  },
  terms: {
    title: 'Terms of Service',
    body: `Última actualización: Abril 2026

Al adquirir el Business-Rescue Kit, aceptas los siguientes términos:

1. Licencia de uso: Recibes una licencia personal e intransferible para usar el contenido del Kit en tu negocio.

2. Propiedad intelectual: Todo el contenido del Kit (plantillas, prompts, guías) es propiedad exclusiva de Business-Rescue Kit. Queda prohibida su redistribución o reventa.

3. Uso permitido: Puedes usar las plantillas y herramientas en tu negocio. No puedes revender, compartir o distribuir el material como propio.

4. Disponibilidad: El acceso al Kit es digital e instantáneo. Nos esforzamos por mantener el acceso disponible 24/7, pero no garantizamos disponibilidad ininterrumpida.

5. Modificaciones: Nos reservamos el derecho de actualizar el contenido del Kit para mejorar su calidad.`
  },
  refund: {
    title: 'Refund Policy',
    body: `Última actualización: Abril 2026

IMPORTANTE: Al ser un producto digital de acceso inmediato, no se ofrecen reembolsos una vez el material ha sido visualizado, a menos que se cumpla la garantía de satisfacción de 7 días descrita en la página principal.

Garantía de satisfacción de 7 días:
Si dentro de los primeros 7 días calendario desde tu compra sientes que el Business-Rescue Kit no te ha ayudado a recuperar el control de tu WhatsApp ni a reducir tu estrés operativo, puedes solicitar un reembolso completo.

Cómo solicitar un reembolso bajo la garantía:
• Envíanos un correo dentro de los primeros 7 días después de tu compra.
• Incluye tu nombre y correo electrónico asociado a la compra.
• Procesaremos tu reembolso en un plazo de 3-5 días hábiles.

Condiciones:
• La solicitud debe realizarse dentro del período de 7 días.
• Después de los 7 días, no se aceptarán solicitudes de reembolso bajo ninguna circunstancia, dado que el acceso al material digital ya fue otorgado.
• Esta política aplica por compra, no por usuario.`
  }
};
export function Footer() {
  const [activePolicy, setActivePolicy] = useState<PolicyType>(null);
  return <>
      <footer className="py-10 px-4 border-t border-dark-border bg-dark-card" data-id="element-21">
        <div className="max-w-4xl mx-auto text-center space-y-4" data-id="element-22">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-23">
            <button onClick={() => setActivePolicy('privacy')} className="hover:text-gray-300 transition-colors cursor-pointer" data-id="element-24">
              Privacy Policy
            </button>
            <span className="text-dark-border" data-id="element-25">|</span>
            <button onClick={() => setActivePolicy('terms')} className="hover:text-gray-300 transition-colors cursor-pointer" data-id="element-26">
              Terms of Service
            </button>
            <span className="text-dark-border" data-id="element-27">|</span>
            <button onClick={() => setActivePolicy('refund')} className="hover:text-gray-300 transition-colors cursor-pointer" data-id="element-28">
              Refund Policy
            </button>
          </div>
          <p className="text-xs text-gray-600" data-id="element-29">
            © 2026 Business-Rescue Kit. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Policy Modal */}
      <AnimatePresence data-id="element-30">
        {activePolicy && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActivePolicy(null)} data-id="element-31">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-dark-card border border-dark-border rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-32">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-dark-border" data-id="element-33">
                <h3 className="text-xl font-bold text-white" data-id="element-34">
                  {policyContent[activePolicy].title}
                </h3>
                <button onClick={() => setActivePolicy(null)} className="p-2 rounded-lg hover:bg-dark-lighter transition-colors text-gray-400 hover:text-white" data-id="element-35">
                  <XIcon className="w-5 h-5" data-id="element-36" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-37">
                <p className="text-gray-400 text-sm leading-relaxed whitespace-pre-line" data-id="element-38">
                  {policyContent[activePolicy].body}
                </p>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-dark-border" data-id="element-39">
                <button onClick={() => setActivePolicy(null)} className="w-full py-3 text-sm font-semibold text-white bg-dark-lighter rounded-xl hover:bg-dark-border transition-colors" data-id="element-40">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}